<template>
  <transition name="slide-y-transition" appear mode="out-in">
    <v-card
      tile
      class="mb-4 d-flex flex-column"
      :class="[
        $style.card,
        isMissing && $style.missing,
        (isNew || isDiff) && $style.new
      ]"
    >
      <div class="d-flex flex-grow-1 justify-space-between px-4 py-2">
        <div
          class="d-flex align-center justify-center"
          :class="[
            $style.iconContainer,
            isMissing && $style.missingBg,
            (isNew || isDiff) && $style.newBg
          ]"
        >
          <v-icon color="white" small v-if="isMissing">mdi-minus</v-icon>
          <div v-else>
            <transition name="scale-transition" mode="out-in">
              <v-icon
                color="white"
                v-if="checked"
                :key="checked"
                @click="setChecked(false)"
                >mdi-check</v-icon
              >
              <div v-else @click="setChecked(true)" :class="$style.checkbox" />
            </transition>
          </div>
        </div>
        <div class="d-flex flex-column">
          <span class="text-subtitle-2 text--secondary">Tåg</span>
          <span class="font-weight-medium">{{ itemData.train_number }}</span>
        </div>
        <div class="d-flex flex-column">
          <span class="text-subtitle-2 text--secondary">Avvikelse</span>
          <span class="font-weight-medium">{{
            trainDeviationTypes[itemData.deviation_type]
          }}</span>
        </div>
        <div class="d-flex flex-column" v-if="!isCanceledStop">
          <span class="text-subtitle-2 text--secondary">Sträcka</span>
          <span class="font-weight-medium"
            >{{ itemData.from }} - {{ itemData.to }}</span
          >
        </div>
        <div class="d-flex flex-column" v-if="isCanceledStop">
          <span class="text-subtitle-2 text--secondary">Platser</span>
          <span class="font-weight-medium">{{ itemData.places }}</span>
        </div>
        <div class="d-flex flex-column" v-if="!isCanceledStop">
          <span class="text-subtitle-2 text--secondary">Tid</span>
          <span class="font-weight-medium"
            >{{ itemData.departure | timeFormat }} -
            {{ itemData.arrival | timeFormat }}</span
          >
        </div>
        <div class="d-flex flex-column" v-if="showCancellationFields">
          <!-- <span class="text-subtitle-2 text--secondary">Orsak</span> -->
          <SmsMessageReasonField :uuid="item.uuid" :item="item" />
        </div>
      </div>

      <v-divider></v-divider>

      <SmsMessageTemplateField
        :uuid="item.uuid"
        :item="item"
        :item-data="itemData"
      />

      <v-divider></v-divider>

      <SmsMessageTextField
        :uuid="item.uuid"
        :item="item"
        :item-data="itemData"
        :validating-form="validatingForm"
      />

      <v-divider></v-divider>

      <div
        class="text-caption text--secondary px-2 py-2 pl-4"
        v-if="!smsSuggestion?.bookings.length"
      >
        Hittar ingen resenärsinformation hittades
      </div>
      <div class="text-caption text--black px-2 py-2 pl-4" v-else>
        <template v-for="(booking, key) of groupedBookings">
          <div :key="key">
            <strong>{{ booking.length }}st</strong> resenärer på sträckan
            {{ key }}
          </div>
        </template>
      </div>
    </v-card>
  </transition>
</template>

<script>
import trainDeviationTypes from '@/common/trainDeviationTypes'
import { format } from 'date-fns'
import { get } from 'vuex-pathify'
import SmsMessageReasonField from './SmsMessageReasonField'
import SmsMessageTemplateField from './SmsMessageTemplateField'
import SmsMessageTextField from './SmsMessageTextField'
import gql from 'graphql-tag'

export default {
  name: 'SmsMessageTripCard',

  props: {
    validatingForm: Boolean,
    item: Object,
    refForm: null,
    type: {
      type: String,
      default: () => 'current'
    }
  },

  components: {
    SmsMessageReasonField,
    SmsMessageTemplateField,
    SmsMessageTextField
  },

  filters: {
    timeFormat(date) {
      return format(new Date(date), 'HH:mm')
    }
  },

  apollo: {
    searchTrain: {
      query: gql`
        query searchTrain($filter: SearchTrainInput!) {
          searchTrain(filter: $filter) {
            public_number
            places {
              type
              advertised
              location
              location_long_name
            }
          }
        }
      `,
      fetchPolicy: 'cache-and-network',
      variables() {
        return {
          filter: {
            train_number: this.item.text.train_number,
            date: format(new Date(this.item.text.departure), 'yyyy-MM-dd')
          }
        }
      }
    },
    smsSuggestion: {
      query: gql`
        query GetSmsSuggestion($filter: GetSmsSuggestion!) {
          smsSuggestion(filter: $filter) {
            bookings {
              first_name
              last_name
              ticketid
              orderid
              salesorderid
              phone_number
              train_number
              departure_date
              time_start
              time_end
              place_start
              place_end
            }
          }
        }
      `,
      fetchPolicy: 'cache-and-network',
      variables() {
        return {
          filter: {
            train_number: this.item.text.train_number,
            departure_date: format(
              new Date(this.item.text.departure),
              'yyyy-MM-dd'
            ),
            place_start: this.item.text.from,
            place_end: this.item.text.to,
            time_start: this.item.text.departure,
            time_end: this.item.text.arrival
          }
        }
      }
    }
  },

  data: () => ({
    trainDeviationTypes
  }),

  watch: {
    smsSuggestion(value) {
      const prop = 'bookings'
      const form = Object.assign({}, this.form)
      form[this.item.uuid].data[prop] = value.bookings || []
      this.$store.dispatch('smsMessage/updateForm', {
        uuid: this.item.uuid,
        prop,
        value: value.bookings || []
      })
    }
  },

  methods: {
    setChecked(value) {
      this.$store.dispatch('smsMessage/setFormChecked', {
        uuid: this.item.uuid,
        value
      })
    },

    onRemoveCancelSection(index) {
      this.$store.dispatch('smsMessage/updateForm', {
        uuid: this.item.uuid,
        prop: 'cancel_sections',
        value: Object.assign({}, this.form[this.item.uuid])
          .data.cancel_sections.filter(x => x.index !== index)
          .map((x, index, arr) => {
            if (index === arr.length - 1) {
              return Object.assign(
                {},
                {
                  ...x,
                  index,
                  arrival: +new Date(this.item.text.arrival),
                  to: this.item.text.to
                }
              )
            }
            return Object.assign(
              {},
              {
                ...x,
                index
              }
            )
          })
      })
    }
  },

  computed: {
    model: get('smsMessage/model'),
    form: get('smsMessage/form'),
    diffTrainDeviations: get('smsMessage/diffTrainDeviations'),
    diffTrainDeviationsCancelSections: get(
      'smsMessage/diffTrainDeviationsCancelSections'
    ),

    groupedBookings() {
      return (this.smsSuggestion?.bookings || []).reduce((acc, booking) => {
        const key = `${booking.place_start}-${booking.place_end}`
        if (!acc[key]) {
          acc[key] = []
        }

        acc[key].push(booking)
        return acc
      }, {})
    },

    cancel_sections() {
      return (
        (this.form[this.item.uuid]?.data?.cancel_sections || [])
          .slice()
          .sort((a, b) => a.index - b.index) || []
      )
    },

    checked() {
      return this.form[this.item.uuid]._checked
    },

    duaMessageStatus() {
      const item = this.model?.body?.find(
        x => x?.trip_uuid === this.itemData.uuid
      )

      if (!item) {
        return null
      }

      const messageStatus = {
        ok: item.status === 'Ok',
        notOk: item.status === 'Error',
        unknown: item.status === 'Unknown',
        response_data: item.response_data
      }

      if (messageStatus.ok) {
        messageStatus.response_data = `Status: behandlad${
          item.updated ? ' ' + format(new Date(item.updated), 'HH:mm') : ''
        }`
      }

      return messageStatus
    },

    isDiff() {
      const isOldDiff = this.isCanceledStop || !this.cancel_sections.length

      if (isOldDiff) {
        return Boolean(
          this.diffTrainDeviations?.find(x => x?.trip_uuid === this.item.uuid)
        )
      }
      return Boolean(
        this.diffTrainDeviationsCancelSections?.find(
          x => x?.trip_uuid === this.item.uuid
        )
      )
    },

    isMissing() {
      return this.type === 'missing'
    },

    isNew() {
      return this.type === 'new'
    },

    itemData() {
      return this.item.text
    },

    isCanceledStop() {
      return ['CANCELED_STOP'].includes(this.itemData?.deviation_type)
    },

    showCancellationFields() {
      return ['CANCELED', 'PARTIAL_CANCELLATION', 'CANCELED_STOP'].includes(
        this.itemData?.deviation_type
      )
    },

    showAddCancelSection() {
      return ['CANCELED', 'PARTIAL_CANCELLATION'].includes(
        this.itemData?.deviation_type
      )
    }
  }
}
</script>

<style lang="scss" module>
.new {
  border-left: 4px solid blue !important;
}

.newBg {
  background: blue;
}

.missing {
  border-left: 4px solid red !important;
}

.missingBg {
  background: red;
}

.card {
  position: relative;
}

.iconContainer {
  position: absolute;
  top: 0;
  left: -24px;
  width: 24px;
  height: 24px;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.checkbox {
  background: white;
  border-radius: 2px;
  width: 18px;
  height: 18px;
}
</style>
